import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, Check, Terminal, Zap, Shield, ArrowLeft, ChevronDown, ChevronRight, AlertCircle, Download, RefreshCw, Clipboard, RotateCcw, CheckCircle2, Wrench } from 'lucide-react';
import { useAuth, API } from '../App';
import Header from '../components/layout/Header';

const MCP_URL = 'https://getfray.ai/api/mcp';

// #676: pin mcp-remote to a known-good version for Claude Desktop users.
// The mcp-remote bridge's internal auth cache is version-scoped; floating on
// @latest means every npx cache-clear (e.g. after a system update) re-triggers
// OAuth and produces the "Failed to reconnect" rage-quit. 0.1.37 is the last
// version we've regression-tested against the Fray MCP. When we bump this,
// run a manual smoke test of the published CD config end-to-end first.
const MCP_REMOTE_PINNED_VERSION = '0.1.37';

const CLIENTS = [
  { id: 'claude-desktop', label: 'Claude Desktop', hasOsTabs: true },
  { id: 'claude-code', label: 'Claude Code' },
  { id: 'cursor', label: 'Cursor' },
  { id: 'windsurf', label: 'Windsurf' },
];

function getMagicPrompt(apiKey) {
  const key = apiKey || 'YOUR_API_KEY';
  return `I need you to set up the Fray MCP server AND the Fray Agent Skill. Please follow these steps in order:

Prerequisites: Desktop Commander must be enabled in your Tools menu before starting. If it's not enabled, enable it now and then paste this prompt again.

Step 1 — Detect OS and locate the config file
Determine whether this is Windows or macOS, then read the Claude Desktop config file:
* Windows: %APPDATA%\\Claude\\claude_desktop_config.json
* macOS: ~/Library/Application Support/Claude/claude_desktop_config.json
Preserve any existing mcpServers entries in the file.

Step 2 — Add the Fray MCP server entry
Merge the following entry into the mcpServers object and write the file back.
NOTE: mcp-remote is pinned to ${MCP_REMOTE_PINNED_VERSION} — do NOT change
this to @latest. The pin is a deliberate stability choice (#676):

{
  "fray": {
    "command": "npx",
    "args": [
      "-y",
      "mcp-remote@${MCP_REMOTE_PINNED_VERSION}",
      "${MCP_URL}?api_key=${key}",
      "--transport", "http-only"
    ]
  }
}

On Windows, if npx is not on PATH, use the full path:
"command": "C:\\\\Users\\\\USERNAME\\\\AppData\\\\Roaming\\\\npm\\\\npx.cmd"

On macOS, if npx is not on PATH, use: /opt/homebrew/bin/npx

Step 3 — Install the Fray Agent Skill
Create the skills directory and download the Committee Design skill:
* macOS/Linux: mkdir -p ~/.claude/skills/fray && curl -H 'Authorization: Bearer ${key}' https://www.getfray.ai/api/skill/committee-design/download -o ~/.claude/skills/fray/SKILL.md
* Windows (PowerShell): New-Item -ItemType Directory -Force -Path "$env:USERPROFILE\\.claude\\skills\\fray"; Invoke-WebRequest -Headers @{'Authorization'='Bearer ${key}'} -Uri 'https://www.getfray.ai/api/skill/committee-design/download' -OutFile "$env:USERPROFILE\\.claude\\skills\\fray\\SKILL.md"
Confirm the file was created and is non-empty by reading the first few lines.

Step 4 — Tell the user to quit and restart
Print exactly: "Setup complete. Please fully quit Claude Desktop now (Cmd+Q on macOS, Alt+F4 on Windows — not just closing the window) and then relaunch it. After it restarts, paste the verify prompt below into a new conversation."

Step 5 — Display the verify prompt
After the restart instruction, output the following verify prompt for the user to copy:

---
Verify my Fray MCP setup by running these two checks:
1. Run fray_diagnostics — this checks connectivity, database, API keys, and model availability. All sections should show healthy status.
2. Run fray_of_the_day — this runs an actual AI debate on a fun daily topic and confirms the full pipeline is working end-to-end.
Report the results of both. If both complete successfully, Fray MCP is fully operational.
---

Do not attempt to call any Fray tools in this conversation — they will not be available until after the restart.
Server name: fray
API Key: ${key}`;
}

const VERIFY_PROMPT = `Verify my Fray MCP setup by running these two checks:

1. Run fray_diagnostics — this checks connectivity, database, API keys, and model availability. All sections should show healthy status.

2. Run fray_of_the_day — this runs an actual AI debate on a fun daily topic and confirms the full pipeline is working end-to-end.

Report the results of both. If both complete successfully, Fray MCP is fully operational.`;

function getManualConfig(client, os, apiKey) {
  const key = apiKey || 'YOUR_API_KEY';
  const url = `${MCP_URL}?api_key=${key}`;

  if (client === 'claude-code') {
    // #676: native HTTP transport — no mcp-remote stdio bridge. Claude Code
    // talks directly to the Fray MCP via streamable HTTP. This bypasses an
    // entire class of bridge bugs (token-cache collisions, stdio pipe hangs,
    // `fray_whoami takes 16 minutes`). Auth via bearer header rather than
    // query-string api_key so the token doesn't appear in process listings.
    return `claude mcp add --transport http fray "${MCP_URL}" --header "Authorization: Bearer ${key}"`;
  }

  const npxCmd = os === 'win' ? 'C:\\\\Users\\\\USERNAME\\\\AppData\\\\Roaming\\\\npm\\\\npx.cmd' : 'npx';
  // #676: pin mcp-remote to MCP_REMOTE_PINNED_VERSION (not @latest) so a
  // silent upstream bump can't brick every CD customer overnight. Also force
  // --transport http-only (mcp-remote 0.1.38 rejects bare `sse` with a
  // fallback that's confusing to debug; http-only is what the Fray backend
  // actually implements).
  return JSON.stringify({
    mcpServers: {
      fray: {
        command: npxCmd,
        args: [
          "-y",
          `mcp-remote@${MCP_REMOTE_PINNED_VERSION}`,
          url,
          "--transport", "http-only",
        ],
      },
    },
  }, null, 2);
}

function getManualConfigPath(client, os) {
  if (client === 'claude-code') return null;
  if (client === 'claude-desktop') {
    return os === 'win'
      ? '%APPDATA%\\Claude\\claude_desktop_config.json'
      : '~/Library/Application Support/Claude/claude_desktop_config.json';
  }
  if (client === 'cursor') return '.cursor/mcp.json';
  if (client === 'windsurf') return '~/.codeium/windsurf/mcp_config.json';
  return '';
}

function CopyButton({ text, label, copied, onCopy, disabled, variant = 'default' }) {
  const variants = {
    default: 'bg-[#2A2A2A] text-[#F5F1E8] hover:bg-[#333]',
    primary: 'bg-[#FF4D00] text-[#0A0A0A] hover:bg-[#FF6B33]',
    success: 'bg-[#22C55E] text-[#0A0A0A] hover:bg-[#22C55E]/80',
  };
  const isCopied = copied === label;
  return (
    <button
      onClick={() => onCopy(text, label)}
      disabled={disabled}
      className={`px-3 py-2 min-h-[44px] flex items-center gap-1.5 text-xs font-['IBM_Plex_Mono'] font-medium transition-colors ${
        disabled ? 'bg-[#2A2A2A] text-[#555] cursor-not-allowed' : variants[variant]
      }`}
    >
      {isCopied ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
    </button>
  );
}

function StepIndicator({ number, label, active, completed }) {
  return (
    <div className="flex items-center gap-3">
      <div className={`w-7 h-7 flex items-center justify-center text-xs font-bold shrink-0 transition-colors ${
        completed ? 'bg-[#22C55E] text-[#0A0A0A]'
          : active ? 'bg-[#FF4D00] text-[#0A0A0A]'
          : 'bg-[#2A2A2A] text-[#555]'
      }`}>
        {completed ? <Check className="w-3.5 h-3.5" /> : number}
      </div>
      <span className={`text-sm font-['Inter'] font-medium ${
        active || completed ? 'text-[#F5F1E8]' : 'text-[#555]'
      }`}>{label}</span>
    </div>
  );
}

export default function McpSetup() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [apiKey, setApiKey] = useState('');
  const [isNewKey, setIsNewKey] = useState(false);
  const [keyFullyVisible, setKeyFullyVisible] = useState(false);
  const [keys, setKeys] = useState([]);
  const [copied, setCopied] = useState('');
  const [regenerating, setRegenerating] = useState(false);
  const [activeStep, setActiveStep] = useState(1);
  const [path, setPath] = useState('magic');
  const [manualClient, setManualClient] = useState('claude-desktop');
  const [manualOs, setManualOs] = useState(() => navigator.platform?.includes('Win') ? 'win' : 'mac');
  const [downloadableSkills, setDownloadableSkills] = useState([]);

  useEffect(() => {
    API.get('/api/skills?downloadable=true').then(r => setDownloadableSkills(r.data.skills || [])).catch(() => {});
  }, []);

  useEffect(() => {
    API.get('/api/developer/keys')
      .then(async (res) => {
        const keyList = res.data.keys || [];
        setKeys(keyList);
        if (keyList.length > 0) {
          const firstActive = keyList.find(k => k.is_active);
          // Auto-regenerate the invite-acceptance key (#553) so the user sees
          // their full key immediately. Fail-safe ordering (#811): POST new key
          // FIRST, then delete ONLY the auto-generated key by id. Never iterate
          // the full list — users may have unrelated keys that must be preserved.
          if (firstActive && firstActive.name && firstActive.name.includes('auto-generated')) {
            setRegenerating(true);
            let newKeyData = null;
            try {
              const newRes = await API.post('/api/developer/keys', { name: 'MCP Setup' });
              newKeyData = newRes.data;
            } catch (e) {
              setRegenerating(false);
              setApiKey(firstActive.key_prefix + '...');
              setKeyFullyVisible(false);
              alert('Could not create a new API key. Your existing key has been preserved. Please try again or contact support.');
              return;
            }
            setApiKey(newKeyData.key);
            setIsNewKey(true);
            setKeyFullyVisible(true);
            let deleted = false;
            try {
              await API.delete(`/api/developer/keys/${firstActive.id}`);
              deleted = true;
            } catch (e) {
              alert('New key created, but the old auto-generated key could not be deleted. You may want to revoke it manually in Settings.');
            }
            // Only drop the old key from local state if server-side delete succeeded;
            // otherwise keep it visible so the UI matches the server (#811 Q2).
            const remaining = deleted ? keyList.filter(k => k.id !== firstActive.id) : keyList;
            setKeys([
              { ...newKeyData, key_prefix: newKeyData.key?.substring(0, 12) },
              ...remaining,
            ]);
            setRegenerating(false);
          } else {
            setApiKey(keyList[0].key_prefix + '...');
            setKeyFullyVisible(false);
          }
        } else {
          API.post('/api/developer/keys', { name: 'MCP Setup' })
            .then(res => {
              setApiKey(res.data.key);
              setIsNewKey(true);
              setKeyFullyVisible(true);
            })
            .catch(() => {
              alert('Could not create an API key. Please try again or contact support.');
            });
        }
      })
      .catch(() => {
        alert('Could not load your API keys. Please refresh the page.');
      });
  }, []);

  const copyToClipboard = (text, label) => {
    if (!keyFullyVisible && text.includes(apiKey)) return;
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const handleRegenerate = async () => {
    // Mirrors the useEffect fail-safe ordering (#811): POST a new key FIRST,
    // then DELETE only the current auto-generated key by id. Never iterate
    // the full `keys` list — custom/personal keys the user created for other
    // integrations must be preserved.
    setRegenerating(true);
    const firstActive = keys[0];
    let newKeyData = null;
    try {
      const res = await API.post('/api/developer/keys', { name: 'MCP Setup' });
      newKeyData = res.data;
    } catch (e) {
      setRegenerating(false);
      alert('Could not create a new API key. Your existing keys have been preserved. Please try again or contact support.');
      return;
    }
    setApiKey(newKeyData.key);
    setIsNewKey(true);
    setKeyFullyVisible(true);

    let deleted = false;
    if (firstActive && firstActive.name && firstActive.name.includes('auto-generated')) {
      try {
        await API.delete(`/api/developer/keys/${firstActive.id}`);
        deleted = true;
      } catch (e) {
        alert('New key created, but the old auto-generated key could not be deleted. You may want to revoke it manually in Settings.');
      }
    }
    const remaining = deleted ? keys.filter(k => k.id !== firstActive.id) : keys;
    setKeys([
      { ...newKeyData, key_prefix: newKeyData.key?.substring(0, 12) },
      ...remaining,
    ]);
    setRegenerating(false);
  };

  const resolvedKey = keyFullyVisible ? apiKey : 'YOUR_API_KEY';
  const magicPrompt = getMagicPrompt(resolvedKey);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Header />
      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Back + Header */}
        <button onClick={() => navigate('/settings')}
          className="flex items-center gap-1.5 text-xs font-['IBM_Plex_Mono'] text-[#A0A0A0] hover:text-[#F5F1E8] mb-4 min-h-[44px]">
          <ArrowLeft className="w-4 h-4" /> Back to Settings
        </button>

        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold font-['Barlow_Condensed'] uppercase tracking-wide flex items-center gap-3 text-[#F5F1E8]">
            <Terminal className="w-7 h-7 text-[#FF4D00]" /> MCP Setup
          </h1>
          <p className="text-sm font-['Inter'] text-[#A0A0A0] mt-1">Connect Fray to your AI client in minutes</p>
        </div>

        {/* API Key Banner */}
        {isNewKey && keyFullyVisible && (
          <div className="mb-6 p-4 bg-[#22C55E]/10 border border-[#22C55E]/30" data-testid="new-key-banner">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-[#22C55E] shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold font-['Inter'] text-[#22C55E]">API Key Generated</p>
                <p className="text-xs font-['Inter'] text-[#A0A0A0] mt-1">
                  Your key is embedded in the prompts below. Copy them now — the full key won't be shown again.
                </p>
                <code className="text-xs font-['IBM_Plex_Mono'] text-[#22C55E] bg-[#22C55E]/5 px-2 py-1 mt-2 block break-all">
                  {apiKey}
                </code>
              </div>
            </div>
          </div>
        )}

        {apiKey && !keyFullyVisible && (
          <div className="mb-6 p-4 bg-[#F2A900]/10 border border-[#F2A900]/30" data-testid="masked-key-banner">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-[#F2A900] shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold font-['Inter'] text-[#F2A900]">API Key Masked</p>
                <p className="text-xs font-['Inter'] text-[#A0A0A0] mt-1">
                  Your key is <code className="font-['IBM_Plex_Mono'] text-[#F2A900]">{apiKey}</code> — regenerate to get copyable prompts with your full key.
                </p>
                <button onClick={handleRegenerate} disabled={regenerating}
                  className="mt-2 px-3 py-1.5 min-h-[36px] bg-[#F2A900] text-[#0A0A0A] font-bold text-xs font-['Inter'] hover:bg-[#D4920A] disabled:opacity-50"
                  data-testid="regenerate-key-btn">
                  <RefreshCw className={`w-3 h-3 inline mr-1.5 -mt-0.5 ${regenerating ? 'animate-spin' : ''}`} />
                  {regenerating ? 'Regenerating...' : 'Regenerate Key'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Two-path toggle */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setPath('magic')}
            className={`flex-1 px-4 py-3 min-h-[52px] text-sm font-['Inter'] font-medium border transition-colors ${
              path === 'magic'
                ? 'bg-[#FF4D00]/10 border-[#FF4D00] text-[#FF4D00]'
                : 'bg-[#1E1E1E] border-[#2A2A2A] text-[#A0A0A0] hover:border-[#555]'
            }`}
          >
            <Zap className="w-4 h-4 inline mr-2 -mt-0.5" />
            Magic Prompt
            {path === 'magic' && <span className="text-[10px] font-['IBM_Plex_Mono'] ml-2 text-[#22C55E]">RECOMMENDED</span>}
          </button>
          <button
            onClick={() => setPath('manual')}
            className={`flex-1 px-4 py-3 min-h-[52px] text-sm font-['Inter'] font-medium border transition-colors ${
              path === 'manual'
                ? 'bg-[#00D4FF]/10 border-[#00D4FF] text-[#00D4FF]'
                : 'bg-[#1E1E1E] border-[#2A2A2A] text-[#A0A0A0] hover:border-[#555]'
            }`}
          >
            <Wrench className="w-4 h-4 inline mr-2 -mt-0.5" />
            Manual Setup
          </button>
        </div>

        {/* MAGIC PROMPT PATH */}
        {path === 'magic' && (
          <div className="space-y-4">

            {/* Step 1: Copy the magic prompt */}
            <div className={`border p-4 md:p-5 transition-colors ${activeStep >= 1 ? 'border-[#FF4D00]/30 bg-[#1E1E1E]' : 'border-[#2A2A2A] bg-[#1E1E1E]/50'}`} data-testid="magic-step-1">
              <button onClick={() => setActiveStep(1)} className="flex items-center justify-between w-full min-h-[44px]">
                <StepIndicator number={1} label="Copy the Magic Prompt" active={activeStep === 1} completed={activeStep > 1} />
                <ChevronDown className={`w-4 h-4 text-[#A0A0A0] transition-transform ${activeStep === 1 ? 'rotate-180' : ''}`} />
              </button>
              {activeStep === 1 && (
                <div className="mt-4 ml-10">
                  <p className="text-xs font-['Inter'] text-[#A0A0A0] mb-3">
                    This prompt tells Claude how to configure the Fray MCP server automatically. Your API key and server URL are already embedded.
                  </p>
                  <div className="relative">
                    <pre className="text-xs font-['IBM_Plex_Mono'] text-[#E0E0E0] bg-[#0A0A0A] p-4 border border-[#2A2A2A] whitespace-pre-wrap max-h-48 overflow-y-auto">
                      {magicPrompt}
                    </pre>
                    <div className="absolute top-2 right-2">
                      <CopyButton text={magicPrompt} label="magic-prompt" copied={copied} onCopy={copyToClipboard} disabled={!keyFullyVisible} variant="primary" />
                    </div>
                  </div>
                  <button onClick={() => setActiveStep(2)}
                    className="mt-3 px-4 py-2 min-h-[44px] bg-[#FF4D00] text-[#0A0A0A] text-xs font-['Inter'] font-medium hover:bg-[#FF6B33] transition-colors">
                    Next <ChevronRight className="w-3.5 h-3.5 inline ml-1" />
                  </button>
                </div>
              )}
            </div>

            {/* Step 2: Paste into Claude */}
            <div className={`border p-4 md:p-5 transition-colors ${activeStep >= 2 ? 'border-[#FF4D00]/30 bg-[#1E1E1E]' : 'border-[#2A2A2A] bg-[#1E1E1E]/50'}`} data-testid="magic-step-2">
              <button onClick={() => setActiveStep(2)} className="flex items-center justify-between w-full min-h-[44px]">
                <StepIndicator number={2} label="Paste into Claude" active={activeStep === 2} completed={activeStep > 2} />
                <ChevronDown className={`w-4 h-4 text-[#A0A0A0] transition-transform ${activeStep === 2 ? 'rotate-180' : ''}`} />
              </button>
              {activeStep === 2 && (
                <div className="mt-4 ml-10">
                  <p className="text-xs font-['Inter'] text-[#A0A0A0] mb-3">
                    Open <strong className="text-[#F5F1E8]">Claude Desktop</strong> or <strong className="text-[#F5F1E8]">Claude Code</strong> and paste the prompt into a new conversation. Claude will configure the MCP server and download the Fray skill — all automatically in one go.
                  </p>
                  <div className="p-3 bg-[#0A0A0A] border border-[#2A2A2A]">
                    <div className="flex items-center gap-2 text-xs font-['IBM_Plex_Mono'] text-[#00D4FF]">
                      <Clipboard className="w-4 h-4" />
                      <span>Paste the magic prompt and Claude handles everything</span>
                    </div>
                  </div>
                  <button onClick={() => setActiveStep(3)}
                    className="mt-3 px-4 py-2 min-h-[44px] bg-[#FF4D00] text-[#0A0A0A] text-xs font-['Inter'] font-medium hover:bg-[#FF6B33] transition-colors">
                    Next <ChevronRight className="w-3.5 h-3.5 inline ml-1" />
                  </button>
                </div>
              )}
            </div>

            {/* Step 3: Restart Claude */}
            <div className={`border p-4 md:p-5 transition-colors ${activeStep >= 3 ? 'border-[#F2A900]/30 bg-[#1E1E1E]' : 'border-[#2A2A2A] bg-[#1E1E1E]/50'}`} data-testid="magic-step-3">
              <button onClick={() => setActiveStep(3)} className="flex items-center justify-between w-full min-h-[44px]">
                <StepIndicator number={3} label="Restart Claude" active={activeStep === 3} completed={activeStep > 3} />
                <ChevronDown className={`w-4 h-4 text-[#A0A0A0] transition-transform ${activeStep === 3 ? 'rotate-180' : ''}`} />
              </button>
              {activeStep === 3 && (
                <div className="mt-4 ml-10">
                  <p className="text-xs font-['Inter'] text-[#A0A0A0] mb-3">
                    When Claude tells you setup is complete, fully quit and relaunch Claude Desktop (<span className="text-[#F5F1E8]">Cmd+Q</span> on macOS, <span className="text-[#F5F1E8]">Alt+F4</span> on Windows — not just close the window). Start a new conversation before proceeding.
                  </p>
                  <div className="p-3 bg-[#F2A900]/10 border border-[#F2A900]/30">
                    <p className="text-xs font-['Inter'] text-[#F2A900]">
                      <RotateCcw className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
                      Fray tools won't be available until after restart. Don't try to use them in the current conversation.
                    </p>
                  </div>
                  <button onClick={() => setActiveStep(4)}
                    className="mt-3 px-4 py-2 min-h-[44px] bg-[#FF4D00] text-[#0A0A0A] text-xs font-['Inter'] font-medium hover:bg-[#FF6B33] transition-colors">
                    Next <ChevronRight className="w-3.5 h-3.5 inline ml-1" />
                  </button>
                </div>
              )}
            </div>

            {/* Step 4: Verify */}
            <div className={`border p-4 md:p-5 transition-colors ${activeStep >= 4 ? 'border-[#22C55E]/30 bg-[#22C55E]/5' : 'border-[#2A2A2A] bg-[#1E1E1E]/50'}`} data-testid="magic-step-4">
              <button onClick={() => setActiveStep(4)} className="flex items-center justify-between w-full min-h-[44px]">
                <StepIndicator number={4} label="Verify Connection" active={activeStep === 4} completed={activeStep >= 4} />
                <ChevronDown className={`w-4 h-4 text-[#A0A0A0] transition-transform ${activeStep === 4 ? 'rotate-180' : ''}`} />
              </button>
              {activeStep === 4 && (
                <div className="mt-4 ml-10">
                  <p className="text-xs font-['Inter'] text-[#A0A0A0] mb-3">
                    In your <strong className="text-[#F5F1E8]">new conversation</strong> after restart, paste this verify prompt:
                  </p>
                  <div className="relative">
                    <pre className="text-xs font-['IBM_Plex_Mono'] text-[#E0E0E0] bg-[#0A0A0A] p-4 border border-[#22C55E]/30 whitespace-pre-wrap">{VERIFY_PROMPT}</pre>
                    <div className="absolute top-2 right-2">
                      <CopyButton text={VERIFY_PROMPT} label="verify" copied={copied} onCopy={(text, label) => { navigator.clipboard.writeText(text); setCopied(label); setTimeout(() => setCopied(''), 2000); }} variant="success" />
                    </div>
                  </div>
                  <div className="mt-3 p-4 bg-[#22C55E]/10 border border-[#22C55E]/30">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#22C55E] shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-['Inter'] font-bold text-[#22C55E]">Setup Complete</p>
                        <p className="text-xs font-['Inter'] text-[#A0A0A0] mt-1">
                          If both checks pass, Fray MCP is ready. Try asking:
                        </p>
                        <code className="text-xs font-['IBM_Plex_Mono'] text-[#00D4FF] bg-[#00D4FF]/10 px-2 py-1 mt-2 block">
                          Run a fray on "Should we migrate to microservices?"
                        </code>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Connection troubleshooting */}
            <div className="p-4 bg-[#1E1E1E] border border-[#2A2A2A]">
              <p className="text-xs font-['Inter'] font-bold text-[#F2A900] mb-2">Connection Lost?</p>
              <p className="text-[10px] font-['Inter'] text-[#A0A0A0] mb-2">
                If Fray tools stop responding after a deploy or timeout:
              </p>
              <ol className="text-[10px] font-['Inter'] text-[#A0A0A0] space-y-1 list-decimal ml-4">
                <li>Clear stale auth cache: <code className="font-['IBM_Plex_Mono'] text-[#00D4FF] bg-[#00D4FF]/10 px-1">rm -rf ~/.mcp-auth</code></li>
                <li>Fully quit Claude Desktop (Cmd+Q / Alt+F4)</li>
                <li>Relaunch and start a new conversation</li>
                <li>Test with: <code className="font-['IBM_Plex_Mono'] text-[#00D4FF] bg-[#00D4FF]/10 px-1">fray_test_connection</code></li>
              </ol>
              <p className="text-[10px] font-['Inter'] text-[#A0A0A0] mt-2">
                Fray uses <strong className="text-[#F5F1E8]">Streamable HTTP</strong> — each tool call is independent, so connections survive server updates.
              </p>
            </div>
          </div>
        )}

        {/* MANUAL SETUP PATH */}
        {path === 'manual' && (
          <div className="space-y-4">
            {/* Platform selector */}
            <div className="p-4 bg-[#1E1E1E] border border-[#2A2A2A]">
              <h3 className="text-sm font-['Inter'] font-semibold text-[#F5F1E8] mb-3">Choose Your Client</h3>
              <div className="flex flex-wrap gap-2 mb-4">
                {CLIENTS.map(c => (
                  <button key={c.id} onClick={() => setManualClient(c.id)}
                    className={`px-3 py-2 min-h-[44px] text-xs font-['IBM_Plex_Mono'] border transition-colors ${
                      manualClient === c.id
                        ? 'text-[#FF4D00] border-[#FF4D00] bg-[#FF4D00]/10'
                        : 'text-[#F5F1E8] border-[#2A2A2A] hover:border-[#555]'
                    }`}>
                    {c.label}
                  </button>
                ))}
              </div>

              {/* OS toggle */}
              {CLIENTS.find(c => c.id === manualClient)?.hasOsTabs && (
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-[10px] font-['IBM_Plex_Mono'] text-[#A0A0A0]">Platform:</span>
                  {[['mac', 'macOS'], ['win', 'Windows']].map(([id, label]) => (
                    <button key={id} onClick={() => setManualOs(id)}
                      className={`px-3 py-1.5 text-xs font-['IBM_Plex_Mono'] border transition-colors ${
                        manualOs === id ? 'text-[#FF4D00] border-[#FF4D00]/40 bg-[#FF4D00]/5' : 'text-[#A0A0A0] border-[#2A2A2A]'
                      }`}>{label}</button>
                  ))}
                </div>
              )}

              {/* Server URL */}
              <div className="mb-4">
                <label className="text-[10px] font-['IBM_Plex_Mono'] text-[#A0A0A0] mb-1 block uppercase tracking-wider">Server URL</label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-3 py-2 text-xs font-['IBM_Plex_Mono'] bg-[#0A0A0A] border border-[#2A2A2A] text-[#00D4FF]">{MCP_URL}</code>
                  <CopyButton text={MCP_URL} label="url" copied={copied} onCopy={(text, label) => { navigator.clipboard.writeText(text); setCopied(label); setTimeout(() => setCopied(''), 2000); }} />
                </div>
              </div>

              {/* Config snippet */}
              <div className="mb-3">
                <label className="text-[10px] font-['IBM_Plex_Mono'] text-[#A0A0A0] mb-1 block uppercase tracking-wider">
                  {manualClient === 'claude-code' ? 'Terminal Command' : 'Configuration'}
                </label>
                {getManualConfigPath(manualClient, manualOs) && (
                  <p className="text-xs font-['Inter'] text-[#A0A0A0] mb-2">
                    Add to: <code className="font-['IBM_Plex_Mono'] text-[#FF4D00] bg-[#FF4D00]/10 px-1">{getManualConfigPath(manualClient, manualOs)}</code>
                  </p>
                )}
                <div className="relative">
                  <pre className="text-xs font-['IBM_Plex_Mono'] text-[#E0E0E0] bg-[#0A0A0A] p-4 border border-[#2A2A2A] whitespace-pre-wrap overflow-x-auto">
                    {getManualConfig(manualClient, manualOs, resolvedKey)}
                  </pre>
                  <div className="absolute top-2 right-2">
                    <CopyButton
                      text={getManualConfig(manualClient, manualOs, resolvedKey)}
                      label="manual-config"
                      copied={copied}
                      onCopy={copyToClipboard}
                      disabled={!keyFullyVisible}
                    />
                  </div>
                </div>
                {manualOs === 'win' && manualClient !== 'claude-code' && (
                  <p className="text-[10px] font-['Inter'] text-[#F2A900] mt-2">
                    Replace <code className="font-['IBM_Plex_Mono']">USERNAME</code> with your actual Windows username.
                  </p>
                )}
              </div>
            </div>

            {/* Install skills */}
            {downloadableSkills.length > 0 && (
              <div className="p-4 bg-[#1E1E1E] border border-[#00D4FF]/20">
                <div className="flex items-center gap-2 mb-3">
                  <Download className="w-4 h-4 text-[#00D4FF]" />
                  <h3 className="text-sm font-['Inter'] font-semibold text-[#F5F1E8]">Install Agent Skills</h3>
                </div>
                <p className="text-xs font-['Inter'] text-[#A0A0A0] mb-3">
                  Skills teach your AI client how to design effective Fray debate committees.
                </p>
                {downloadableSkills.map(skill => {
                  const curlCmd = `mkdir -p .claude/skills/fray && curl -H 'Authorization: Bearer ${resolvedKey}' \\\n  https://getfray.ai/api/skill/${skill.slug}/download \\\n  -o .claude/skills/fray/SKILL.md`;
                  return (
                    <div key={skill.slug} className="p-3 bg-[#0A0A0A] border border-[#2A2A2A] mb-2">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-['Inter'] font-bold text-[#F5F1E8]">{skill.name}</span>
                      </div>
                      <div className="relative">
                        <pre className="text-[10px] font-['IBM_Plex_Mono'] text-[#00D4FF] bg-[#1E1E1E] p-2 border border-[#2A2A2A] whitespace-pre-wrap">{curlCmd}</pre>
                        <button onClick={() => copyToClipboard(curlCmd.replace(/\\\n\s*/g, ''), `skill-${skill.slug}`)}
                          disabled={!keyFullyVisible}
                          className={`absolute top-1 right-1 px-2 py-1 text-[10px] font-['IBM_Plex_Mono'] ${keyFullyVisible ? 'bg-[#2A2A2A] text-[#F5F1E8]' : 'bg-[#1E1E1E] text-[#555] cursor-not-allowed'}`}>
                          {copied === `skill-${skill.slug}` ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Restart + Verify */}
            <div className="p-4 bg-[#1E1E1E] border border-[#2A2A2A]">
              <div className="flex items-center gap-2 mb-3">
                <RotateCcw className="w-4 h-4 text-[#F2A900]" />
                <h3 className="text-sm font-['Inter'] font-semibold text-[#F5F1E8]">Restart & Verify</h3>
              </div>
              <p className="text-xs font-['Inter'] text-[#A0A0A0] mb-3">
                Fully quit and relaunch your client, then paste this verify prompt in a new conversation:
              </p>
              <div className="relative">
                <pre className="text-xs font-['IBM_Plex_Mono'] text-[#E0E0E0] bg-[#0A0A0A] p-4 border border-[#22C55E]/30 whitespace-pre-wrap">{VERIFY_PROMPT}</pre>
                <div className="absolute top-2 right-2">
                  <CopyButton text={VERIFY_PROMPT} label="verify-manual" copied={copied} onCopy={(text, label) => { navigator.clipboard.writeText(text); setCopied(label); setTimeout(() => setCopied(''), 2000); }} variant="success" />
                </div>
              </div>
            </div>

            {/* Connection troubleshooting */}
            <div className="p-4 bg-[#1E1E1E] border border-[#2A2A2A]">
              <p className="text-xs font-['Inter'] font-bold text-[#F2A900] mb-2">Connection Lost?</p>
              <ol className="text-[10px] font-['Inter'] text-[#A0A0A0] space-y-1 list-decimal ml-4">
                <li>Clear auth cache: <code className="font-['IBM_Plex_Mono'] text-[#00D4FF] bg-[#00D4FF]/10 px-1">rm -rf ~/.mcp-auth</code></li>
                <li>Fully quit and relaunch your client</li>
                <li>Test with: <code className="font-['IBM_Plex_Mono'] text-[#00D4FF] bg-[#00D4FF]/10 px-1">fray_test_connection</code></li>
              </ol>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
